import { useState, useEffect } from 'react';
import { Select, Tooltip } from 'antd';
import {
  FileTextOutlined,
  DatabaseOutlined,
  SafetyCertificateOutlined,
  BankOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { getData } from '../../config/api-connect';
import { getColumnSearchProps, getColumnFilters } from '../../utils/filters';
import SummaryLayout from '../../components/layout/SummaryLayout';
import MonthSelector from '../../components/ui/MonthSelector';
import { useMonthFilter } from '../../hooks/useMonthFilter';
import { formatCurrency, renderWithEllipsis, uniqueValues } from '../../utils/helper';

const { Option } = Select;

function B2BInvoices() {
  const { selectedMonth, setSelectedMonth } = useMonthFilter();

  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [venues, setVenues] = useState([]);
  const [venuelist, setVenuelist] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    getData('/finance/get_cities_and_venues')
      .then((response) => {
        const allCities = response.cities || [];
        const allVenues = response.venues || [];

        setCities(allCities);
        setVenues(allVenues);
        setVenuelist(allVenues);

        const storedCity = localStorage.getItem('selectedCity');

        if (storedCity) {
          setSelectedCityId(JSON.parse(storedCity));
        } else {
          setSelectedCityId('');
        }

        setSelectedVenueId('');
        setInitialized(true);
      })
      .catch((error) => console.error('Error:', error.message));
  }, []);

  useEffect(() => {
    if (selectedCityId) {
      setVenuelist(venues.filter((item) => item.city === selectedCityId));
      const currentVenue = venues.find((v) => v.id === selectedVenueId);
      if (currentVenue && currentVenue.city !== selectedCityId) {
        setSelectedVenueId('');
      }
    } else {
      setVenuelist(venues);
    }
  }, [selectedCityId, venues]);

  useEffect(() => {
    if (!initialized) return;
    getGstInvoices();
  }, [initialized, selectedCityId, selectedVenueId, selectedMonth]);

  const getGstInvoices = () => {
    setLoading(true);

    const month = selectedMonth.format('MM');
    const year = selectedMonth.format('YYYY');
    const cityQuery = selectedCityId ? `&city_id=${selectedCityId}` : '';
    const venueQuery = selectedVenueId ? `&venue_id=${selectedVenueId}` : '';
    const typeQuery = `&type=B2B`;

    getData(
      `/finance/get_gst_invoices/?month=${month}&year=${year}${cityQuery}${venueQuery}${typeQuery}`,
    )
      .then((response) => {
        setInvoices(response.result || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.error('Error fetching invoices:', error.message);
      });
  };

  const columns = [
    {
      title: 'Verified',
      dataIndex: 'is_verified',
      align: 'center',
      fixed: 'right',
      width: 90,
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (isVerified) => (
        <Tooltip title={isVerified ? 'Collection verified' : 'Collection not verified'}>
          {isVerified ? (
            <CheckCircleOutlined
              style={{
                color: '#52c41a',
                fontSize: '18px',
              }}
            />
          ) : (
            <CloseCircleOutlined
              style={{
                color: '#ff4d4f',
                fontSize: '18px',
              }}
            />
          )}
        </Tooltip>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      align: 'left',
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      ...getColumnSearchProps('customer'),
      onFilter: (value, record) => {
        const name = record?.customer ? record.customer.toString().toLowerCase() : '';
        const mobile = record?.mobile_no ? record.mobile_no.toString().toLowerCase() : '';
        return name.includes(value.toLowerCase()) || mobile.includes(value.toLowerCase());
      },
      render: (text, record) => (
        <>
          <div>{text ?? <span style={{ color: '#aaa' }}>—</span>}</div>
          <div style={{ color: 'dimgrey', marginTop: 4 }}>{record?.mobile_no ?? ''}</div>
        </>
      ),
    },
    {
      title: 'Sales Model',
      dataIndex: 'sales_model',
      align: 'center',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      ...getColumnFilters('sales_model', uniqueValues(invoices, 'sales_model')),
    },
    {
      title: 'Slab',
      dataIndex: 'slab',
      align: 'center',
      ...getColumnFilters('slab', uniqueValues(invoices, 'slab')),
    },
    {
      title: 'Taxable Value',
      dataIndex: 'taxable_value',
      align: 'right',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      sorter: (a, b) => (Number(a.taxable_value) || 0) - (Number(b.taxable_value) || 0),
      render: (val) => (val == null ? '—' : formatCurrency(val)),
    },
    {
      title: 'CGST',
      dataIndex: 'cgst',
      align: 'right',
      render: (val) => (val == null ? '—' : formatCurrency(val)),
    },
    {
      title: 'SGST',
      dataIndex: 'sgst',
      align: 'right',
      render: (val) => (val == null ? '—' : formatCurrency(val)),
    },
    {
      title: 'Total (incl. GST)',
      dataIndex: 'total',
      align: 'right',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      sorter: (a, b) => (Number(a.total) || 0) - (Number(b.total) || 0),
      render: (val) =>
        val == null ? (
          <span style={{ color: '#aaa' }}>—</span>
        ) : (
          <strong>{formatCurrency(val)}</strong>
        ),
    },
    {
      title: 'Venue',
      dataIndex: 'venue',
      align: 'left',
      width: 250,
      render: (venue) => renderWithEllipsis(venue?.name, { width: 200, lines: 2 }),
      ...getColumnFilters('venue.name', uniqueValues(invoices, 'venue', 'name')),
    },
    {
      title: 'Invoice Number',
      dataIndex: 'invoice_number',
      align: 'center',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      ...getColumnSearchProps('invoice_number'),
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
    },
    {
      title: '',
      dataIndex: 'pdf',
      align: 'center',
      fixed: 'right',
      render: (pdf) => {
        return (
          <Tooltip title={pdf ? 'View Invoice PDF' : 'Invoice PDF not available'}>
            <EyeOutlined
              style={{
                fontSize: '18px',
                color: pdf ? '#1890ff' : '#ccc',
                cursor: pdf ? 'pointer' : 'not-allowed',
              }}
              onClick={(e) => {
                if (!pdf) return;
                e.stopPropagation();
                window.open(pdf, '_blank', 'noopener,noreferrer');
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  const pageTitle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span>B2B Invoices</span>
    </div>
  );

  const chooseCity = (value) => {
    setSelectedCityId(value);
    setSelectedVenueId('');
    localStorage.setItem('selectedCity', JSON.stringify(value));
  };

  const renderRightSideActions = () => (
    <>
      <Select
        showSearch
        placeholder="Select City"
        style={{ width: 170, marginRight: 8 }}
        value={selectedCityId}
        onChange={(value) => chooseCity(value)}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
      >
        {cities.map((city) => (
          <Option key={city.id} value={city.id}>
            {city.name}
          </Option>
        ))}
      </Select>

      <Select
        showSearch
        placeholder="Select Venue"
        style={{ width: 250, marginRight: 8 }}
        value={selectedVenueId}
        onChange={(value) => setSelectedVenueId(value)}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
      >
        <Option value="">All Venues</Option>
        {venuelist.map((venue) => (
          <Option key={venue.id} value={venue.id}>
            {venue.name}
          </Option>
        ))}
      </Select>

      <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />
    </>
  );

  const totalTaxable = invoices.reduce((acc, curr) => acc + (Number(curr.taxable_value) || 0), 0);
  const totalGst = invoices.reduce(
    (acc, curr) => acc + (Number(curr.cgst) || 0) + (Number(curr.sgst) || 0),
    0,
  );
  const totalAmount = invoices.reduce((acc, curr) => acc + (Number(curr.total) || 0), 0);

  const statsConfig = [
    {
      key: 's_invoices',
      title: 'Total Invoices',
      icon: <FileTextOutlined style={{ fontSize: 32, color: '#1890ff' }} />,
    },
    {
      key: 's_taxable',
      title: 'Total Taxable Value',
      icon: <DatabaseOutlined style={{ fontSize: 32, color: '#1565c0' }} />,
    },
    {
      key: 's_gst',
      title: 'Total GST Collected',
      icon: <SafetyCertificateOutlined style={{ fontSize: 32, color: '#faad14' }} />,
    },
    {
      key: 's_total',
      title: 'Total Amount (incl. GST)',
      icon: <BankOutlined style={{ fontSize: 32, color: '#52c41a' }} />,
    },
  ];

  const totals = {
    s_invoices: invoices.length,
    s_taxable: `₹ ${totalTaxable.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
    s_gst: `₹ ${totalGst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
    s_total: `₹ ${totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
  };

  return (
    <>
      <SummaryLayout
        title={pageTitle}
        headerContent={renderRightSideActions()}
        statsConfig={statsConfig}
        totals={totals}
        statsLoading={loading}
        tableProps={{
          rowKey: 'id',
          columns,
          data: invoices,
          loading: loading,
          pagination: { pageSize: 20, showSizeChanger: false },
          onRow: (record) => ({
            style: { cursor: 'pointer' },
          }),
        }}
      />
    </>
  );
}

export default B2BInvoices;
